import { useState } from 'react';
import {
    CheckCircle2,
    Circle,
    FileText,
    Shield,
    ExternalLink,
    AlertTriangle,
} from 'lucide-react';

// ==================== Types ====================
export interface ConsentItem {
    id: string;
    label: string;
    description?: string;
    documentUrl?: string;
    documentLabel?: string;
    required: boolean;
}

interface ConsentCheckboxesProps {
    items?: ConsentItem[];
    onConsentChange: (allConsented: boolean, consentMap: Record<string, boolean>) => void;
    className?: string;
    disabled?: boolean;
}

// ==================== Default Items ====================
const defaultConsentItems: ConsentItem[] = [
    {
        id: 'terms',
        label: 'Li e concordo com os Termos de Uso',
        description: 'Os termos definem as regras de utilização da plataforma',
        documentUrl: '#',
        documentLabel: 'Termos de Uso',
        required: true,
    },
    {
        id: 'privacy',
        label: 'Li e concordo com a Política de Privacidade',
        description:
            'Autorizo o tratamento dos meus dados pessoais conforme a LGPD (Lei 13.709/2018)',
        documentUrl: '#',
        documentLabel: 'Política de Privacidade',
        required: true,
    },
    {
        id: 'contract',
        label: 'Li e concordo com todas as cláusulas do contrato',
        description:
            'Declaro ter lido atentamente todas as cláusulas do Contrato de Licenciamento SaaS e estar de acordo com seus termos',
        required: true,
    },
];

// ==================== Component ====================
export const ConsentCheckboxes: React.FC<ConsentCheckboxesProps> = ({
    items = defaultConsentItems,
    onConsentChange,
    className = '',
    disabled = false,
}) => {
    const [consents, setConsents] = useState<Record<string, boolean>>(
        items.reduce((acc, item) => ({ ...acc, [item.id]: false }), {})
    );
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    const handleConsentChange = (id: string, checked: boolean) => {
        const newConsents = { ...consents, [id]: checked };
        setConsents(newConsents);
        setTouched({ ...touched, [id]: true });

        // Check if all required consents are given
        const requiredItems = items.filter((item) => item.required);
        const allConsented = requiredItems.every((item) => newConsents[item.id]);

        onConsentChange(allConsented, newConsents);
    };

    const handleOpenDocument = (url: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const requiredItems = items.filter((item) => item.required);
    const consentedCount = requiredItems.filter((item) => consents[item.id]).length;
    const allConsented = consentedCount === requiredItems.length;

    return (
        <div className={`${className}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-sky-400" />
                    <h3 className="font-semibold text-white">Termos e Condições</h3>
                </div>
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-white/50">
                        {consentedCount}/{requiredItems.length}
                    </span>
                    {allConsented ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    ) : (
                        <Circle className="w-4 h-4 text-white/30" />
                    )}
                </div>
            </div>

            {/* Consent Items */}
            <div className="space-y-3">
                {items.map((item) => {
                    const isChecked = consents[item.id];
                    const isTouched = touched[item.id];
                    const showError = item.required && isTouched && !isChecked;

                    return (
                        <div
                            key={item.id}
                            className={`p-4 rounded-xl border transition-all ${isChecked
                                ? 'bg-emerald-500/10 border-emerald-500/30'
                                : showError
                                    ? 'bg-red-500/10 border-red-500/30'
                                    : 'bg-white/5 border-white/10 hover:border-white/20'
                                }`}
                        >
                            <label className="flex items-start gap-3 cursor-pointer">
                                {/* Custom Checkbox */}
                                <div className="relative mt-0.5">
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => handleConsentChange(item.id, e.target.checked)}
                                        className="sr-only"
                                        disabled={disabled}
                                    />
                                    <div
                                        className={`w-5 h-5 rounded flex items-center justify-center transition-all ${isChecked
                                            ? 'bg-emerald-500 border-emerald-500'
                                            : 'bg-white/5 border-2 border-white/30'
                                            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {isChecked && (
                                            <svg
                                                className="w-3 h-3 text-white"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth={3}
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        )}
                                    </div>
                                </div>

                                {/* Label & Description */}
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span
                                            className={`text-sm font-medium ${isChecked ? 'text-emerald-300' : 'text-white'
                                                }`}
                                        >
                                            {item.label}
                                        </span>
                                        {item.required && (
                                            <span className="text-xs text-red-400">*obrigatório</span>
                                        )}
                                    </div>

                                    {item.description && (
                                        <p className="mt-1 text-xs text-white/50">{item.description}</p>
                                    )}

                                    {item.documentUrl && item.documentUrl !== '#' && (
                                        <button
                                            onClick={(e) => handleOpenDocument(item.documentUrl!, e)}
                                            className="mt-2 inline-flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300 transition-colors"
                                        >
                                            <FileText className="w-3 h-3" />
                                            {item.documentLabel || 'Ver documento'}
                                            <ExternalLink className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            </label>

                            {/* Error Message */}
                            {showError && (
                                <div className="flex items-center gap-1.5 mt-2 ml-8 text-red-400">
                                    <AlertTriangle className="w-3 h-3" />
                                    <span className="text-xs">Este aceite é obrigatório</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Warning if not all consented */}
            {!allConsented && (
                <div className="mt-4 flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-300">
                        Você precisa aceitar todos os termos obrigatórios para prosseguir com a
                        assinatura.
                    </p>
                </div>
            )}

            {/* Success Message */}
            {allConsented && (
                <div className="mt-4 flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <p className="text-sm text-emerald-300">
                        Todos os termos foram aceitos. Você pode prosseguir com a assinatura.
                    </p>
                </div>
            )}
        </div>
    );
};
